/**
 * Loyalty Service — Supabase as single source of truth.
 * Staff writes sessions here. Customers read from here.
 * No localStorage sync needed for session data.
 */

import { createClient } from '@/lib/supabase/client';
import type { Customer, LoyaltyCard } from '@/data/mockData';

const supabase = createClient();

// ── Types matching DB schema ──────────────────────────────────────────────────

interface DBCustomer {
  id: string;
  email: string;
  name: string;
  joined_at: string;
  auth_user_id?: string | null;
}

interface DBCard {
  id: string;
  customer_email: string;
  card_number: number;
  start_date: string;
  end_date: string | null;
  reward_25_earned: boolean;
  reward_25_redeemed_at: string | null;
  reward_50_earned: boolean;
  reward_50_redeemed_at: string | null;
  is_active: boolean;
  salon_name?: string;
}

interface DBSession {
  id: string;
  card_id: string;
  customer_email: string;
  session_date: string;
  session_time: string;
  seller_name: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function dbCardToLoyaltyCard(card: DBCard, sessions: DBSession[]): LoyaltyCard {
  return {
    id: card.id,
    cardNumber: card.card_number,
    customerId: card.customer_email,
    startDate: card.start_date,
    endDate: card.end_date,
    sessions: sessions.map(s => ({
      id: s.id,
      date: s.session_date,
      time: s.session_time,
      seller: s.seller_name,
    })),
    reward25Earned: card.reward_25_earned,
    reward25RedeemedAt: card.reward_25_redeemed_at,
    reward50Earned: card.reward_50_earned,
    reward50RedeemedAt: card.reward_50_redeemed_at,
    isActive: card.is_active,
    salonName: card.salon_name || 'Loyalo Studio',
  };
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Ensure a customer record exists in Supabase.
 * Called when staff scans a QR — creates the customer if not found.
 */
export async function ensureCustomerExists(email: string, name: string, joinedAt: string): Promise<void> {
  const { error } = await supabase
    .from('loyalty_customers')
    .upsert({ email: email.toLowerCase(), name, joined_at: joinedAt }, { onConflict: 'email', ignoreDuplicates: true });
  if (error) console.error('[loyalty] ensureCustomerExists error:', error.message);
}

/**
 * Link a Supabase auth user to a loyalty customer record.
 * Called after customer registers/logs in via Supabase auth.
 */
export async function linkCustomerAuthUser(email: string, authUserId: string): Promise<void> {
  const lowerEmail = email.toLowerCase();
  // Ensure customer record exists first
  const today = new Date().toISOString().split('T')[0];
  const { error: upsertErr } = await supabase
    .from('loyalty_customers')
    .upsert(
      { email: lowerEmail, name: lowerEmail.split('@')[0], joined_at: today, auth_user_id: authUserId },
      { onConflict: 'email' }
    );
  if (upsertErr) console.error('[loyalty] linkCustomerAuthUser error:', upsertErr.message);
}

/**
 * Ensure an active loyalty card exists for the customer.
 * Returns the active card's DB id.
 * Each salon gets its own independent card — identified by salonName.
 * salonName is set when creating a new card (from the staff who first scans).
 */
export async function ensureActiveCard(email: string, salonName?: string): Promise<string | null> {
  const lowerEmail = email.toLowerCase();
  const resolvedSalon = salonName || 'Loyalo Studio';

  // Check for existing active card for THIS specific salon
  const { data: existingCards, error: fetchErr } = await supabase
    .from('loyalty_cards')
    .select('id, salon_name')
    .eq('customer_email', lowerEmail)
    .eq('is_active', true)
    .order('card_number', { ascending: false });

  if (fetchErr) {
    console.error('[loyalty] ensureActiveCard fetch error:', fetchErr.message);
    return null;
  }

  // Find an active card that belongs to this salon
  const salonCard = existingCards?.find(c => c.salon_name === resolvedSalon);
  if (salonCard) return salonCard.id;

  // Count existing cards to determine card_number
  const { count } = await supabase
    .from('loyalty_cards')
    .select('id', { count: 'exact', head: true })
    .eq('customer_email', lowerEmail);

  const today = new Date().toISOString().split('T')[0];
  const { data: newCard, error: insertErr } = await supabase
    .from('loyalty_cards')
    .insert({
      customer_email: lowerEmail,
      card_number: (count || 0) + 1,
      start_date: today,
      is_active: true,
      salon_name: resolvedSalon,
    })
    .select('id')
    .single();

  if (insertErr) {
    console.error('[loyalty] ensureActiveCard insert error:', insertErr.message);
    return null;
  }

  return newCard?.id || null;
}

/**
 * Add a session to the active card.
 * Updates reward flags and closes card if 10 sessions reached.
 * Returns the updated Customer object.
 */
export async function addSessionToCard(
  cardId: string,
  customerEmail: string,
  sellerName: string,
  salonName?: string
): Promise<{ success: boolean; sessionCount: number }> {
  const lowerEmail = customerEmail.toLowerCase();
  const now = new Date();
  const sessionDate = now.toISOString().split('T')[0];
  const sessionTime = now.toTimeString().slice(0, 5);

  // Insert session
  const { error: sessErr } = await supabase
    .from('loyalty_sessions')
    .insert({
      card_id: cardId,
      customer_email: lowerEmail,
      session_date: sessionDate,
      session_time: sessionTime,
      seller_name: sellerName,
    });

  if (sessErr) {
    console.error('[loyalty] addSessionToCard insert error:', sessErr.message);
    return { success: false, sessionCount: 0 };
  }

  // Update salon_name on card if provided and card still has default name
  if (salonName) {
    await supabase
      .from('loyalty_cards')
      .update({ salon_name: salonName })
      .eq('id', cardId)
      .in('salon_name', ['Loyalo Studio', '']); // only update if still default
  }

  // Count sessions on this card
  const { count } = await supabase
    .from('loyalty_sessions')
    .select('id', { count: 'exact', head: true })
    .eq('card_id', cardId);

  const sessionCount = count || 0;

  // Update reward flags and close card if complete
  const updates: Partial<DBCard> = {
    reward_25_earned: sessionCount >= 5,
    reward_50_earned: sessionCount >= 10,
    is_active: sessionCount < 10,
    end_date: sessionCount >= 10 ? sessionDate : null,
  };

  const { error: updateErr } = await supabase
    .from('loyalty_cards')
    .update(updates)
    .eq('id', cardId);

  if (updateErr) {
    console.error('[loyalty] addSessionToCard update error:', updateErr.message);
  }

  return { success: true, sessionCount };
}

/**
 * Mark a reward as redeemed on a card.
 */
export async function redeemReward(cardId: string, rewardType: '25%' | '50%'): Promise<boolean> {
  const today = new Date().toISOString().split('T')[0];
  const updates = rewardType === '25%'
    ? { reward_25_redeemed_at: today }
    : { reward_50_redeemed_at: today };

  const { error } = await supabase
    .from('loyalty_cards')
    .update(updates)
    .eq('id', cardId);

  if (error) {
    console.error('[loyalty] redeemReward error:', error.message);
    return false;
  }
  return true;
}

/**
 * Fetch full customer data from Supabase by email.
 * Returns a Customer object compatible with existing UI components.
 */
export async function fetchCustomerByEmail(email: string): Promise<Customer | null> {
  const lowerEmail = email.toLowerCase();

  // Fetch customer record
  const { data: dbCustomer, error: custErr } = await supabase
    .from('loyalty_customers')
    .select('*')
    .eq('email', lowerEmail)
    .single();

  if (custErr || !dbCustomer) return null;

  // Fetch all cards
  const { data: dbCards, error: cardsErr } = await supabase
    .from('loyalty_cards')
    .select('*')
    .eq('customer_email', lowerEmail)
    .order('card_number', { ascending: true });

  if (cardsErr || !dbCards) return null;

  // Fetch all sessions for all cards
  const cardIds = dbCards.map(c => c.id);
  const { data: dbSessions } = await supabase
    .from('loyalty_sessions')
    .select('*')
    .in('card_id', cardIds)
    .order('session_date', { ascending: true });

  const sessions: DBSession[] = dbSessions || [];

  const loyaltyCards: LoyaltyCard[] = dbCards.map(card => {
    const cardSessions = sessions.filter(s => s.card_id === card.id);
    return dbCardToLoyaltyCard(card as DBCard, cardSessions);
  });

  return {
    id: dbCustomer.id,
    name: dbCustomer.name,
    email: lowerEmail,
    joinedAt: dbCustomer.joined_at,
    loyaltyCards,
  };
}

/**
 * Fetch customer by Supabase auth user ID.
 */
export async function fetchCustomerByAuthId(authUserId: string): Promise<Customer | null> {
  const { data: dbCustomer, error } = await supabase
    .from('loyalty_customers')
    .select('email')
    .eq('auth_user_id', authUserId)
    .single();

  if (error || !dbCustomer) return null;
  return fetchCustomerByEmail(dbCustomer.email);
}

/**
 * Fetch customer by email, creating them if they don't exist yet.
 * Used by staff scan flow.
 * Each salon gets its own independent active card.
 */
export async function fetchOrCreateCustomer(
  email: string,
  name: string,
  joinedAt: string,
  salonName?: string
): Promise<Customer | null> {
  await ensureCustomerExists(email, name, joinedAt);
  const customer = await fetchCustomerByEmail(email);
  if (!customer) return null;

  const resolvedSalon = salonName || 'Loyalo Studio';

  // Check if this salon already has an active card for this customer
  const hasSalonActiveCard = customer.loyaltyCards.some(c => c.isActive && c.salonName === resolvedSalon);

  if (!hasSalonActiveCard) {
    // Create a new card for this salon
    await ensureActiveCard(email, resolvedSalon);
    return fetchCustomerByEmail(email);
  }
  return customer;
}

/**
 * Subscribe to real-time updates for a customer's sessions.
 * Returns an unsubscribe function.
 */
export function subscribeToCustomerSessions(
  email: string,
  onUpdate: () => void
): () => void {
  const lowerEmail = email.toLowerCase();

  const channel = supabase
    .channel(`loyalty_sessions_${lowerEmail.replace(/[^a-z0-9]/g, '_')}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'loyalty_sessions',
        filter: `customer_email=eq.${lowerEmail}`,
      },
      () => onUpdate()
    )
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'loyalty_cards',
        filter: `customer_email=eq.${lowerEmail}`,
      },
      () => onUpdate()
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}
